import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { CheckCircle2, Loader2, XCircle, FileText, CreditCard, Phone, CalendarDays, Car, Landmark } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "../../mock";
import { Logo } from "./Navbar";
import { money } from "./AdminQuote";
import SlotPicker from "./QuoteSlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ label, value, strong, red, testid }) => (
  <div className={`flex justify-between py-1.5 ${strong ? "text-base font-bold text-white" : "text-sm text-white/70"} ${red ? "!text-brand font-bold" : ""}`} data-testid={testid}><span>{label}</span><span>{value}</span></div>
);

const Lines = ({ q }) => {
  const bal = q.balance_paid ? 0 : q.balance;
  return (
    <div className="mt-6 rounded-xl border border-white/10 bg-[#0b0c0f] p-5" data-testid="quote-lines">
      {q.items.map((i, k) => <Row key={k} label={i.description} value={money(i.amount)} />)}
      <div className="my-2 border-t border-white/10" />
      <Row label="Total" value={money(q.total)} strong testid="quote-page-total" />
      {q.paid > 0 && <Row label="Already paid" value={money(-q.paid)} />}
      {q.balance_paid && <Row label="Paid" value={money(-q.balance)} />}
      <Row label={bal < 0 ? (q.refund_amount ? "Refunded to you" : "Refund due to you") : "Balance to pay"} value={money(Math.abs(bal))} red testid="quote-page-balance" />
      {q.note && <p className="mt-3 text-sm italic text-white/55">{q.note}</p>}
    </div>
  );
};

const Note = ({ tone = "ok", children, testid }) => {
  const cls = tone === "ok" ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200" : "border-white/15 bg-white/5 text-white/70";
  const Icon = tone === "ok" ? CheckCircle2 : XCircle;
  return <div className={`mt-6 flex items-start gap-3 rounded-xl border p-4 text-sm ${cls}`} data-testid={testid}><Icon className="mt-0.5 h-5 w-5 shrink-0" /><div>{children}</div></div>;
};

const StatusBox = ({ q }) => {
  const isQ = q.source === "question";
  if (q.booking) return <Note testid="quote-booked">You're booked in for {format(parseISO(q.booking.date), "EEEE d MMMM")} at {q.booking.time} (ref {q.booking.reference}). A confirmation email is on its way.</Note>;
  if (q.status === "accepted") return (
    <Note testid="quote-accepted">
      Quote accepted{isQ ? "" : ", your booking is confirmed"}. {q.refund_amount ? `We've refunded ${money(q.refund_amount)} to your original payment method.` : q.refund_due ? `We'll refund ${money(q.refund_due)} to you shortly.` : q.balance_paid ? "Paid, thank you!" : ""}
      {isQ && q.arrange_requested && " We'll be in touch to arrange a date."}
    </Note>
  );
  if (q.status === "invoiced" && q.balance_paid) return <Note testid="invoice-paid">This invoice is paid, thank you!</Note>;
  if (q.status === "superseded") return <Note testid="quote-superseded">Your booking has been confirmed by us, please check your confirmation email for the details.</Note>;
  if (q.status === "declined") return <Note tone="muted" testid="quote-declined">You declined this quote. We'll be in touch, or call us to talk it through.</Note>;
  return null;
};

const Bank = ({ bank, reference }) => (
  <div className="rounded-xl border border-white/10 bg-[#0b0c0f] p-4 text-sm text-white/70" data-testid="quote-bank">
    <div className="mb-1 flex items-center gap-2 font-semibold text-white"><Landmark className="h-4 w-4 text-brand" />Or pay by bank transfer</div>
    <div>{bank.account_name} · Sort code {bank.sort_code} · Account {bank.account_number}</div>
    <div>Reference: <span className="font-semibold text-white">{reference}</span></div>
  </div>
);

const Header = ({ q }) => {
  const isInvoice = q.doc_type === "invoice" || (q.status === "accepted" && q.source === "booking");
  const when = q.preferred_date ? `${format(parseISO(q.preferred_date), "EEEE d MMMM yyyy")} · ${q.preferred_time} · ${q.service_type === "mobile" ? "Mobile service" : "Workshop visit"}` : null;
  return <>
    <div className="text-[12px] font-semibold uppercase tracking-[0.1em] text-brand">{isInvoice ? "Invoice" : "Your quote"} · {q.reference}</div>
    <h1 className="mt-1 font-display text-3xl font-bold text-white">Hi {q.name.split(" ")[0]}, {q.doc_type === "invoice" ? "here's your invoice" : "here's your price"}</h1>
    <div className="mt-4 space-y-1.5 text-sm text-white/65">
      {when && <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-brand" />{when}</div>}
      {q.vehicle && <div className="flex items-center gap-2"><Car className="h-4 w-4 text-brand" />{q.vehicle}</div>}
    </div>
  </>;
};

const QuotePage = () => {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    axios.get(`${API}/quotes/${token}`).then(({ data }) => setQ(data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load this quote."));
  }, [token]);
  const act = async (action) => {
    setBusy(action);
    try {
      const { data } = await axios.post(`${API}/quotes/${token}/${action}`);
      if (action === "pay-balance") { window.location.href = data.checkout_url; return; }
      setQ(data);
      toast.success({ accept: q.source === "question" ? "Quote accepted! Now pick a date." : "Booking confirmed! Check your email.", decline: "Quote declined", arrange: "Thanks, we'll be in touch to arrange a date." }[action]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong, please try again.");
    }
    setBusy("");
  };
  const canPay = q && ["accepted", "invoiced"].includes(q.status) && q.balance >= 1 && !q.balance_paid;
  const showPicker = q && q.source === "question" && ["accepted", "invoiced"].includes(q.status) && !q.booking && !q.arrange_requested;
  return (
    <div className="flex min-h-screen flex-col bg-[#08090b]">
      <header className="border-b border-white/10"><div className="container-x flex h-[72px] items-center"><Logo /></div></header>
      <main className="flex flex-1 justify-center px-5 py-12">
        <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#111217] p-7" data-testid="quote-page">
          {!q && !err && <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand" />}
          {err && <p className="text-center text-white/70" data-testid="quote-error">{err}</p>}
          {q && <>
            <Header q={q} />
            <Lines q={q} />
            <StatusBox q={q} />
            {showPicker && <SlotPicker token={token} busy={!!busy} onBooked={setQ} onArrange={() => act("arrange")} />}
            <div className="mt-6 flex flex-col gap-2">
              {q.status === "sent" && <>
                <button disabled={!!busy} onClick={() => act("accept")} className="btn-red flex items-center justify-center gap-2 rounded-lg py-3 font-semibold disabled:opacity-60" data-testid="quote-accept-btn">{busy === "accept" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{q.source === "question" ? "Accept quote" : q.booking_status === "pending" ? "Accept quote & confirm booking" : "Accept new price"}</button>
                <button disabled={!!busy} onClick={() => act("decline")} className="rounded-lg border border-white/15 py-3 text-sm font-semibold text-white/70 hover:border-white/40 disabled:opacity-60" data-testid="quote-decline-btn">Decline</button>
              </>}
              {canPay && (
                <button disabled={!!busy} onClick={() => act("pay-balance")} className="btn-red flex items-center justify-center gap-2 rounded-lg py-3 font-semibold disabled:opacity-60" data-testid="quote-pay-balance-btn">{busy === "pay-balance" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}Pay {money(q.balance)} online<span className="hidden sm:inline"> · Card, Klarna, PayPal</span></button>
              )}
              {canPay && q.bank?.account_number && <Bank bank={q.bank} reference={q.reference} />}
              {q.include_pdf && <a href={`${API}/quotes/${token}/pdf`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-lg border border-white/15 py-3 text-sm font-semibold text-white/80 hover:border-white/40" data-testid="quote-pdf-link"><FileText className="h-4 w-4" />Download PDF</a>}
              <a href={BRAND.phoneHref} className="mt-2 flex items-center justify-center gap-2 text-sm text-white/55 hover:text-brand"><Phone className="h-4 w-4" />Questions? {BRAND.phone}</a>
            </div>
          </>}
        </div>
      </main>
    </div>
  );
};

export default QuotePage;
